import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { Sanitaeruebersicht, SanitaerOffenesEnde } from '../api';
import type { Rahmen } from './Projektrahmen';
import { SanitaerBerechnung } from './SanitaerBerechnung';
import { SanitaerTeilstrecken } from './SanitaerTeilstrecken';
import { SanitaerProdukte } from './SanitaerProdukte';
import { SanitaerBaum } from './SanitaerBaum';
import { SanitaerDruckzonen } from './SanitaerDruckzonen';
import { SanitaerAbsicherung } from './SanitaerAbsicherung';
import {
  SanitaerGegenstandsseite, SanitaerAnlagenseite,
} from './SanitaerUebersichten';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, LoadingState,
  StatusBadge, type Spalte,
} from '../muster';

/**
 * Die ehemaligen Reiter von `Sanitaer.tsx` als echte Unterrouten (S1,
 * `auftraege/S1-sanitaer-navigation-und-baum.md`).
 *
 * Jede Funktion hier ist ein duenner Wrapper um eine bereits vorhandene
 * Komponente (`SanitaerBerechnung`, `SanitaerTeilstrecken`, ...) - der
 * Zustand "welcher Reiter" ist die Route geworden, der Inhalt ist derselbe
 * wie vorher. Eine zweite Strukturliste entsteht hier nicht: Welche Route zu
 * welchem Gewerksknoten gehoert, steht ausschliesslich in
 * `navigation/struktur.ts`.
 *
 * Die drei Abwasserrechnungen (Schmutzwasser, Fallleitungen, Hebeanlagen)
 * bleiben eigene Seiten - sie schneiden nach EMPFAENGER
 * (Sanitaerplaner, Rohbauplaner, Anlagenbauer) und nicht nach Anzeige; siehe
 * die Begruendung in `SanitaerBerechnung.tsx` und im Sanitaerdokument.
 */

export function SanitaerTrinkwasser() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="trinkwasser"
      titel="Trinkwasserberechnung"
      einleitung={
        <>
          Spitzendurchfluss nach DIN 1988-300: V̇s = a · ΣV̇R^b + c mit der
          Gebäudekennzahl für Hotel. Der Versorgungsdruck am
          Gebäudeeintritt beträgt 2,4 bar nach Vorgabe des Fachplaners; die
          Druckerhöhung steht als eigene Zeile ganz oben.
        </>
      }
    />
  );
}

export function SanitaerWarmwasser() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="warmwasser"
      titel="Warmwasserauslegung"
      einleitung={
        <>
          Die Frischwasserstationen aus dem Spitzendurchfluss des
          angeschlossenen Netzteils; die Durchlauferhitzer aus ihrer
          Anschlussleistung gegen den Vollstrom des versorgten
          Gegenstands.
          <br />
          Die Auslegungswerte der Stationen sind VORLÄUFIG mit 10 kW und
          15 kPa angesetzt — die Herstellerdaten stehen aus.
        </>
      }
    />
  );
}

export function SanitaerSchmutzwasser() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="schmutzwasser"
      ohneGattungen={['hebeanlage']}
      titel="Schmutzwasserberechnung"
      einleitung={
        <>
          V̇ww = K · √ΣDU nach DIN EN 12056-2 mit K = 0,7. Das Kondensat ist
          enthalten — es ist häusliches Abwasser aus Kühlgeräten und läuft
          in dieselben Sammelleitungen.
          <br />
          Gerechnet wird je NETZTEIL und nicht je Teilstrecke: Das Netz
          zerfällt in 324 Teile, deren größtes ein Achtel enthält. Eine
          Teilstreckenrechnung darauf müsste eine Fließrichtung erfinden.
        </>
      }
    />
  );
}

export function SanitaerFallleitungen() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="abwasser_fall"
      titel="Fallleitungen nach DIN EN 12056-2"
      einleitung={
        <>
          Eine Fallleitung wird nicht über die Teilfüllung bemessen — sie
          hat kein Gefälle. Ihr Abflussvermögen steht in einer TABELLE je
          Nennweite und Lüftungsart (DIN EN 12056-2, Tabelle 11, Füllungs­grad
          0,33).
          <br />
          Erkannt werden die Fallleitungen über die GRUNDRISSLÄNGE und
          nicht über die Leitungsart: Die hängt an der Zeichenrichtung des
          Rohres und trifft nur einen Teil der senkrechten
          Schmutzwasserrohre. Der Anschlusswert stromaufwärts folgt aus
          der Fließrichtung (V-S1).
        </>
      }
    />
  );
}

export function SanitaerHebeanlagen() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    // Eine eigene Seite - ausdrueckliche Vorgabe. Eine Hebeanlage ist keine
    // Strangrechnung und geht zwischen 350 Netzteilen unter; sie gehoert
    // ausserdem an den Anlagenbauer und nicht an den Sanitaerplaner.
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="schmutzwasser"
      nurGattungen={['hebeanlage']}
      titel="Hebeanlagen Schmutzwasser"
      einleitung={
        <>
          Die Förderhöhe ist geodätisch bis über die Rückstauebene (Annahme des
          Projekts, siehe Annahmen und Vorgaben) zuzüglich der Reibung in der Druckleitung. Der
          Scheitel der Rückstauschleife MUSS darüber liegen — sonst drückt
          der Kanal bei Rückstau in das Gebäude zurück.
          <br />
          Der Förderstrom folgt aus dem Zulauf und ist auf dessen Doppeltes
          gedeckelt. Erreicht die vorhandene Druckleitung damit keine
          0,7 m/s, ist die LEITUNG zu groß — die erforderliche Nennweite
          steht im Rechenweg.
        </>
      }
    />
  );
}

export function SanitaerFettabwasser() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="fettabwasser"
      titel="Fettabwasser mit Abscheidern und Vorbehältern"
      einleitung={
        <>
          NS = Qs · fd · ft · fr nach DIN 4040-100, aufgerundet auf die
          Reihe 1 · 2 · 4 · 7 · 10 · 15 · 20 · 25 · 30. Schlammfang
          100 · NS Liter, Fettspeicher 40 · NS Liter.
          <br />
          Wo im Modell KEIN Abscheider steht, wird die Nenngröße trotzdem
          gerechnet: Die nützliche Auskunft ist, WELCHER Abscheider fehlt,
          und nicht nur, dass einer fehlt.
        </>
      }
    />
  );
}

export function SanitaerRegenwasser() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="regenwasser"
      titel="Regenwasserberechnung"
      einleitung={
        <>
          Das Modell führt KEINE Dachflächen je Ablauf. Deshalb ist die
          Rechnung umgekehrt: Was trägt die vorhandene Leitung, und welcher
          Dachfläche entspricht das? Das Ergebnis lässt sich gegen den
          Dachplan halten.
          <br />
          Berechnungsregen r(5,5) = 300 l/(s·ha), Jahrhundertregen
          r(5,100) = 600 l/(s·ha) — beides ANGENOMMEN. Die Werte für
          München und die Rigolen- beziehungsweise Muldenauslegung stehen
          aus.
        </>
      }
    />
  );
}

/**
 * Der Grund, warum der Überflutungsnachweis (noch) kein PDF hat: Die Route nimmt
 * heute das Behelfskürzel UEB, die Berichtsart SAN-UEB ist bei TX nicht
 * angemeldet (Kürzelregel S3.1). Excel geht - die Mappe braucht kein Kürzel.
 */
export const GRUND_OHNE_UEB_PDF = 'Die Berichtsart SAN-UEB ist bei TX noch nicht angemeldet; bis dahin gibt es kein '
  + 'PDF (der Server nähme nur das Behelfskürzel UEB).';

/**
 * Der Überflutungsnachweis (TS5, Paket TS9b2) - eine Hülle um dieselbe
 * Berechnungsseite, Bereich `ueberflutung`. Vorbehalt AUA-08/SAN-33 über
 * `sanitaerFachvorbehalt` mit dem Satz der Excel-Mappe.
 */
export function SanitaerUeberflutung() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return (
    <SanitaerBerechnung
      api={api} projektId={projekt.id} bereich="ueberflutung"
      titel="Überflutungsnachweis"
      pdfGesperrt={GRUND_OHNE_UEB_PDF}
      einleitung={
        <>
          Überflutungsnachweis nach DIN 1986-100 §14.9: Das zurückzuhaltende
          Volumen je Dauerstufe nach Gl. 20/21 aus der Differenz der
          Regenspenden (KOSTRA) für die Jährlichkeit des Nachweises und die
          der Bemessung, mit der maßgebenden Dauer. Jährlichkeit,
          Flächenaufteilung und schadlos überflutbare Flächen sind
          Annahmen, bis AUA-08 beantwortet ist.
        </>
      }
    />
  );
}

/** Druckzonen, Druckerhöhung und Druckminderer (TS9b2). Siehe `SanitaerDruckzonen.tsx`. */
export function SanitaerDruckzonenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerDruckzonen api={api} projektId={projekt.id} />;
}

/** Die Absicherung nach DIN EN 1717 (TS9b2). Siehe `SanitaerAbsicherung.tsx`. */
export function SanitaerAbsicherungSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerAbsicherung api={api} projektId={projekt.id} />;
}

/**
 * Die Aufschluesselung nach Straengen und Posten (Schritt 3c). Eigene Route
 * und nicht in die Bereichsseiten eingebaut: Sie zeigen die KOPFZAHLEN je
 * Gattung, hier geht es um den Weg dahin.
 */
export function SanitaerTeilstreckenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerTeilstrecken api={api} projektId={projekt.id} />;
}

/**
 * Der Baum: Geschoss → Raum → Gegenstandsart → Gegenstand. Siehe
 * `SanitaerBaum.tsx`.
 */
export function SanitaerVerortungSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerBaum api={api} projektId={projekt.id} />;
}

/**
 * Die Gesamtuebersicht je Etage - zur Verifizierung des Spitzendurchflusses.
 * Siehe Kopfkommentar in `SanitaerUebersichten.tsx`.
 */
export function SanitaerGegenstaendeSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerGegenstandsseite api={api} projektId={projekt.id} />;
}

export function SanitaerAnlagenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerAnlagenseite api={api} projektId={projekt.id} />;
}

export function SanitaerProdukteSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <SanitaerProdukte api={api} projektId={projekt.id} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// Befunde: die offenen Rohrenden
// ═══════════════════════════════════════════════════════════════════════════

const KREISNAMEN: Record<string, string> = {
  trinkwasser_kalt: 'Trinkwasser kalt',
  trinkwasser_warm: 'Trinkwasser warm',
  zirkulation: 'Zirkulation',
  schmutzwasser: 'Schmutzwasser',
  schmutzwasser_grund: 'Schmutzwasser-Grundleitung',
  entlueftung: 'Entlüftung',
  kondensat: 'Kondensat',
  regenwasser: 'Regenwasser',
  notentwaesserung: 'Notentwässerung',
  druckleitung: 'Druckleitung',
  fettabwasser: 'Fettabwasser',
};

const EINSTUFUNGEN: Record<string, { name: string; gut: boolean; was: string }> = {
  netzende_belueftung: {
    name: 'Netzende Belüftung', gut: true,
    was: 'Kopf einer Lüftungsleitung über Dach — richtig offen.',
  },
  anschluss_gegenstand: {
    name: 'Anschluss an Gegenstand', gut: true,
    was: 'Ein Sanitärgegenstand liegt innerhalb des Grenzabstands — belegt. '
       + 'Die Verbindung liegt im verknüpften Modell und kann in Revit gar '
       + 'nicht bestehen.',
  },
  anschluss_vermutet: {
    name: 'Anschluss vermutet', gut: true,
    was: 'Im selben Raum steht ein wasserführender Gegenstand, aber keiner '
       + 'innerhalb des Grenzabstands. Bei einem wandhängenden WC ist das der '
       + 'Normalfall: Einfügepunkt an der Wand, Ablaufstutzen im Schacht '
       + 'dahinter. VERMUTET, nicht belegt.',
  },
  uebergang_aussenanlage: {
    name: 'Übergang Außenanlage', gut: true,
    was: 'Dort endet das Modell; die Fortsetzung liegt im Außenanlagenmodell.',
  },
  luecke: {
    name: 'LÜCKE', gut: false,
    was: 'Hier ist das Netz zerteilt. Eine Berechnung würde nur einen Teil '
       + 'erfassen, ohne zu sagen, dass sie es tut.',
  },
  unbestimmt: {
    name: 'unbestimmt', gut: false,
    was: 'Die Regel konnte nicht laufen — meist fehlt eine Angabe. NICHT '
       + 'dasselbe wie eine Lücke: unbestimmt heißt „wir wissen es nicht" und '
       + 'löst eine Pflege aus, Lücke hieße „hier fehlt eine Leitung".',
  },
};

/**
 * Die offenen Rohrenden - eigene Pruefungsseite (`art: pruefung`), wie
 * `lueftung/befunde`. Bis zum 20.09.2026 stand dieser Inhalt oben auf der
 * Sammelseite `Sanitaer.tsx`; die Lueckenzahl selbst steht dort weiterhin
 * als Kennzahl (mit Sprung hierher), die Liste der einzelnen Luecken steht
 * nur noch hier.
 *
 * Sie stehen vor allem anderen, weil eine Lücke die Berechnung still
 * verfälscht: Sie zerteilt das Netz, und was hinter der Lücke liegt, kommt in
 * keiner Summe vor.
 */
export function SanitaerBefunde() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<Sanitaeruebersicht | null>(null);
  const [enden, setEnden] = useState<SanitaerOffenesEnde[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.sanitaerUebersicht(projekt.id)
      .then((x) => { setU(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id]);

  const laden = async () => {
    const x = await api.sanitaerOffeneEnden(projekt.id, 'luecke');
    setEnden(x.zeilen);
  };

  return (
    <>
      <PageHeader
        titel="Sanitär — Befunde"
        beschreibung="Die offenen Rohrenden mit ihrer Einstufung: welche sind richtig offen, welche zerteilen das Netz."
      />

      {fehler && (
        <ErrorState
          meldung="Die Sanitärübersicht konnte nicht geladen werden."
          technisch={fehler}
        />
      )}

      {!u ? (
        <LoadingState text="Befunde werden geladen …" zeilen={6} spalten={3} />
      ) : (
        <OffeneEnden u={u} enden={enden} laden={laden} />
      )}
    </>
  );
}

function OffeneEnden({ u, enden, laden }: {
  u: Sanitaeruebersicht;
  enden: SanitaerOffenesEnde[] | null;
  laden: () => void;
}) {
  if ((u.offene ?? []).length === 0) {
    return (
      <EmptyState
        titel="Kein offenes Rohrende im Bestand."
        erklaerung="Die Einstufung entsteht beim Lesen des Sanitärmodells."
      />
    );
  }

  const luecken = u.offene.find((o) => o.einstufung === 'luecke')?.anzahl ?? 0;

  const einstufungsspalten: Spalte<any>[] = [
    {
      schluessel: 'einstufung', titel: 'Einstufung', art: 'status',
      immerSichtbar: true, breite: '260px',
      wert: (o) => (EINSTUFUNGEN[o.einstufung]?.name ?? o.einstufung),
      inhalt: (o) => (
        <StatusBadge
          zustand={EINSTUFUNGEN[o.einstufung]?.gut ? 'gut' : 'warnung'}
          text={EINSTUFUNGEN[o.einstufung]?.name ?? o.einstufung}
          klein
        />
      ),
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (o) => o.anzahl },
    {
      schluessel: 'was', titel: 'Bedeutung',
      wert: (o) => (EINSTUFUNGEN[o.einstufung]?.was ?? ''),
      inhalt: (o) => (
        <span className="schwach" style={{ fontSize: 12 }}>
          {EINSTUFUNGEN[o.einstufung]?.was ?? ''}
        </span>
      ),
    },
  ];

  const endenspalten: Spalte<SanitaerOffenesEnde>[] = [
    { schluessel: 'elementId', titel: 'ElementId', immerSichtbar: true, wert: (o) => o.revitElementId },
    {
      schluessel: 'kreis', titel: 'Kreis',
      wert: (o) => (o.kreis ? KREISNAMEN[o.kreis] ?? o.kreis : null),
    },
    { schluessel: 'systemtyp', titel: 'Systemtyp', wert: (o) => o.systemtyp },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (o) => o.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (o) => o.raumnummer },
    {
      schluessel: 'dn', titel: 'Nennweite', art: 'zahl', einheit: 'mm',
      wert: (o) => (o.durchmesserMm ? Math.round(o.durchmesserMm) : null),
    },
    {
      schluessel: 'begruendung', titel: 'Begründung', wert: (o) => o.begruendung,
      inhalt: (o) => (
        <span className="schwach" style={{ fontSize: 12 }}>{o.begruendung ?? ''}</span>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Offene Rohrenden"
      beschreibung={'Sie stehen vor allem anderen, weil eine Lücke die Berechnung '
        + 'still verfälscht: Sie zerteilt das Netz, und was hinter der Lücke liegt, '
        + 'kommt in keiner Summe vor.'}
      aktionen={luecken > 0 ? (
        <button className="m-knopf m-knopf-haupt" onClick={laden}>
          Die {luecken} Lücken ansehen
        </button>
      ) : undefined}
    >
      <DataTable
        name="Einstufungen"
        spalten={einstufungsspalten}
        zeilen={u.offene}
        schluessel={(o) => String(o.einstufung)}
        suche={false}
        spaltenwahl={false}
        dicht
      />

      {enden && (
        <DataTable
          name="Lücken im Netz"
          spalten={endenspalten}
          zeilen={enden}
          schluessel={(o) => `${o.revitElementId}-${o.connectorId}`}
          ausfuhr
          ausfuhrDateiname="sanitaer-luecken"
          dicht
          leer={(
            <EmptyState
              titel="Keine Lücke im Netz."
              erklaerung="Jedes offene Rohrende ist begründet — das Netz ist zusammenhängend."
            />
          )}
        />
      )}
    </ContentSection>
  );
}
